import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useToggleObject, OpenCloseButton, PatientRecurringSessionItem, PatientSingleSessionItem, PatientSingleSession, PatientRecurringSession } from '@uplift/ui-v2';
import { CalendarDateService } from '@uplift/services';

import { useTimezone } from '@uplift/contexts';
import { NextSession } from '@uplift/api';
import { formatUserFullName, getUserFullName } from '@uplift/utils';
import { ModalsEnum, useModalsContext } from '../../../../../app/hooks/modal';


export type AllSessionsProps = {
  sessions: NextSession[];
  nextSessions: NextSession[];
};


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  }
}));


export const AllSessions: React.FC<AllSessionsProps> = (props) => {
  const { value: allShown, toggle: toggleAll } = useToggleObject(false);
  const { classes } = useStyles();
  const timezone = useTimezone();
  const modalStore = useModalsContext();

  const allSessions: (PatientRecurringSession | PatientSingleSession)[] = React.useMemo(() => {
    const now = CalendarDateService.date().utc();

    const groupedByParent = [ ...props.sessions ].reduce<{ [key: string]: NextSession }>((prev, next) => {
      if (next.appointmentSeriesId) {
        if (!prev[next.appointmentSeriesId]) {
          return {
            ...prev,
            [next.appointmentSeriesId]: next,
          };
        }
        return prev;
      }
      return {
        ...prev,
        [next.id]: next,
      };
    }, {});

    const resultSessions = Object.values(groupedByParent).map(s => {
      const start = CalendarDateService.date(s.fromDatetime).tz(timezone);
      const diff = start.diff(now, 'minutes');

      const providerName = formatUserFullName({
        firstName: s.provider.firstName,
        lastName: s.provider.lastName,
        preferredFirstName: s.provider.preferredName
      });

      const providerDisplay = getUserFullName(s.provider.firstName, s.provider.lastName, s.provider.preferredName);

      if (s.appointmentSeriesId && s.recurring) {
        return {
          id: s.appointmentSeriesId,
          start: start,
          end: CalendarDateService.date(s.toDatetime).tz(timezone),
          isMaterialized: s.isMaterialized,
          providerName,
          providerRole: s.provider.medicalRole,
          providerDisplay,
          recurring: s.recurring,
          type: 'recurring' as const,
          order: 0,
        };
      }

      return {
        id: s.id,
        start: start,
        end: CalendarDateService.date(s.toDatetime).tz(timezone),
        isMaterialized: s.isMaterialized,
        providerName,
        providerRole: s.provider.medicalRole,
        providerDisplay,
        connectLink: diff < 30 ? `/appointments/${s.id}/session` : null,
        type: 'single' as const,
        order: 1,
      };
    });

    resultSessions.sort((a, b) => b.order - a.order);

    return resultSessions;
  }, [ props.nextSessions, props.sessions, timezone ]);

  if (!allSessions?.length) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Box width="100%" textAlign="center">
        <OpenCloseButton
          open={allShown}
          openTitle="Show all scheduled sessions"
          closeTitle="Hide all scheduled sessions"
          size="small"
          onClick={toggleAll}
        />
      </Box>
      {allShown && allSessions.map(item => {
        if (item?.type === 'recurring') {
          return (
            <PatientRecurringSessionItem
              key={item.id}
              item={item}
              onCancel={(series) => {
                modalStore.openModal(ModalsEnum.cancelSession, {
                  appointment: {
                    id: item.id,
                    fromDatetime: series.fromDatetime,
                    parentId: series.seriesId,
                    providerName: item.providerDisplay,
                  },
                  cancelMode: 'all'
                });
              }}
              onCancelItem={(seriesItem) => {
                modalStore.openModal(ModalsEnum.cancelSession, {
                  appointment: {
                    id: item.id,
                    fromDatetime: seriesItem.fromDatetime,
                    parentId: seriesItem.seriesId,
                    providerName: item.providerDisplay,
                  },
                  cancelMode: 'manual'
                });
              }}
            />
          );
        }
        if (item?.type === 'single') {
          return (
            <PatientSingleSessionItem
              key={item.id}
              item={item}
              onCancel={(session) => {
                modalStore.openModal(ModalsEnum.cancelSession, {
                  appointment: {
                    id: session.id,
                    fromDatetime: item.start,
                    parentId: item.seriesId,
                    isMaterialized: item.isMaterialized,
                    providerName: item.providerDisplay,
                  },
                  cancelMode: 'single'
                });
              }}
            />
          );
        }

        return null;
      })}
    </Box>
  );
};
